/**
 * KML / KMZ reader.
 *
 * Reads three things, not just geometry:
 *   1. <Folder> / <Document> structure — each folder becomes its OWN layer, so
 *      a KML's internal organisation survives the import instead of collapsing
 *      into one mixed-geometry collection.
 *   2. <Style> / <StyleMap> / <styleUrl> — LineStyle, PolyStyle and IconStyle
 *      are resolved per placemark and turned into a BlueView renderer, so lines
 *      and fills keep the colours the author gave them.
 *   3. Placemark geometry and attributes.
 *
 * Dependency-free: KML is XML (`DOMParser`), KMZ is a ZIP (shared zip reader).
 */

import type { LayerGeometryType, LayerRenderer, RendererCategory } from '../types';
import { readZip } from './zip';

/* ────────────────────────────── XML helpers ─────────────────────────────── */

/** Descendants named `tag`, namespace-agnostic (KML files vary). */
function descendants(node: Element | Document, tag: string): Element[] {
  const direct = Array.from(node.getElementsByTagName(tag));
  if (direct.length > 0) return direct;
  return Array.from(node.getElementsByTagNameNS('*', tag));
}

/** DIRECT children named `tag` — needed so nested folders are not double-counted. */
function childElements(node: Element, tag: string): Element[] {
  return Array.from(node.children).filter(
    (child) => (child.localName || child.tagName.replace(/^.*:/, '')) === tag,
  );
}

function firstDescendant(node: Element, tag: string): Element | undefined {
  return descendants(node, tag)[0];
}

function firstChild(node: Element, tag: string): Element | undefined {
  return childElements(node, tag)[0];
}

function textOf(node: Element | undefined): string {
  return node?.textContent?.trim() ?? '';
}

/* ──────────────────────────────── Styles ────────────────────────────────── */

export interface KmlStyle {
  lineColor?: string;
  lineOpacity?: number;
  lineWidth?: number;
  fillColor?: string;
  fillOpacity?: number;
  /** PolyStyle <fill>0</fill> means outline only. */
  filled?: boolean;
  iconColor?: string;
  iconScale?: number;
  iconHref?: string;
}

/**
 * KML colours are `aabbggrr` — alpha first and the RGB channels REVERSED.
 * Reading them as `#rrggbb` is the classic way to get the wrong colour.
 */
function parseKmlColor(raw: string): { color: string; opacity: number } | undefined {
  const hex = raw.trim().replace(/^#/, '');
  if (!/^[0-9a-fA-F]{8}$/.test(hex)) {
    // Some writers emit 6 digits; treat those as opaque rrggbb.
    if (/^[0-9a-fA-F]{6}$/.test(hex)) return { color: `#${hex}`, opacity: 1 };
    return undefined;
  }
  const alpha = parseInt(hex.slice(0, 2), 16) / 255;
  const blue = hex.slice(2, 4);
  const green = hex.slice(4, 6);
  const red = hex.slice(6, 8);
  return { color: `#${red}${green}${blue}`.toLowerCase(), opacity: alpha };
}

function readStyleElement(element: Element): KmlStyle {
  const style: KmlStyle = {};

  const line = firstDescendant(element, 'LineStyle');
  if (line) {
    const colour = parseKmlColor(textOf(firstDescendant(line, 'color')));
    if (colour) {
      style.lineColor = colour.color;
      style.lineOpacity = colour.opacity;
    }
    const width = Number(textOf(firstDescendant(line, 'width')));
    if (Number.isFinite(width) && width > 0) style.lineWidth = width;
  }

  const poly = firstDescendant(element, 'PolyStyle');
  if (poly) {
    const colour = parseKmlColor(textOf(firstDescendant(poly, 'color')));
    if (colour) {
      style.fillColor = colour.color;
      style.fillOpacity = colour.opacity;
    }
    const fill = textOf(firstDescendant(poly, 'fill'));
    style.filled = fill === '' ? true : fill !== '0';
  }

  const icon = firstDescendant(element, 'IconStyle');
  if (icon) {
    const colour = parseKmlColor(textOf(firstDescendant(icon, 'color')));
    if (colour) style.iconColor = colour.color;
    const scale = Number(textOf(firstDescendant(icon, 'scale')));
    if (Number.isFinite(scale) && scale > 0) style.iconScale = scale;
    const href = textOf(firstDescendant(icon, 'href'));
    if (href) style.iconHref = href;
  }

  return style;
}

/**
 * Every shared <Style>, plus <StyleMap> entries resolved to their `normal` pair.
 * Highlight styles are ignored — they describe hover, not the resting look.
 */
function collectStyles(doc: Document): Map<string, KmlStyle> {
  const styles = new Map<string, KmlStyle>();

  for (const element of descendants(doc, 'Style')) {
    const id = element.getAttribute('id');
    if (id) styles.set(id, readStyleElement(element));
  }

  for (const map of descendants(doc, 'StyleMap')) {
    const id = map.getAttribute('id');
    if (!id) continue;
    for (const pair of descendants(map, 'Pair')) {
      if (textOf(firstDescendant(pair, 'key')) !== 'normal') continue;
      const ref = textOf(firstDescendant(pair, 'styleUrl')).replace(/^#/, '');
      const inline = firstDescendant(pair, 'Style');
      const resolved = ref ? styles.get(ref) : inline ? readStyleElement(inline) : undefined;
      if (resolved) styles.set(id, resolved);
    }
  }

  return styles;
}

/** The style that applies to one placemark: inline first, then styleUrl. */
function styleForPlacemark(placemark: Element, shared: Map<string, KmlStyle>): KmlStyle {
  const inline = firstChild(placemark, 'Style');
  if (inline) return readStyleElement(inline);

  const ref = textOf(firstChild(placemark, 'styleUrl')).replace(/^#/, '');
  // A styleUrl may point into another file; only local references resolve.
  return (ref ? shared.get(ref.split('#').pop() ?? ref) : undefined) ?? {};
}

/* ─────────────────────────────── Geometry ───────────────────────────────── */

function parseCoordinates(node: Element | undefined): [number, number][] {
  const raw = textOf(node);
  if (!raw) return [];
  return raw
    .split(/\s+/)
    .map((tuple) => {
      const [lon, lat] = tuple.split(',').map(Number);
      return [lon, lat] as [number, number];
    })
    .filter(([lon, lat]) => Number.isFinite(lon) && Number.isFinite(lat));
}

function ringOf(boundary: Element | undefined): [number, number][] {
  return boundary ? parseCoordinates(firstDescendant(boundary, 'coordinates')) : [];
}

function geometryFrom(element: Element): GeoJSON.Geometry | null {
  const tag = element.localName || element.tagName.replace(/^.*:/, '');

  if (tag === 'Point') {
    const [coord] = parseCoordinates(firstDescendant(element, 'coordinates'));
    return coord ? { type: 'Point', coordinates: coord } : null;
  }
  if (tag === 'LineString' || tag === 'LinearRing') {
    const coords = parseCoordinates(firstDescendant(element, 'coordinates'));
    return coords.length >= 2 ? { type: 'LineString', coordinates: coords } : null;
  }
  if (tag === 'Polygon') {
    const outer = ringOf(firstDescendant(element, 'outerBoundaryIs'));
    if (outer.length < 3) return null;
    const holes = descendants(element, 'innerBoundaryIs')
      .map(ringOf)
      .filter((ring) => ring.length >= 3);
    return { type: 'Polygon', coordinates: [outer, ...holes] };
  }
  if (tag === 'Track') {
    const coords = descendants(element, 'coord')
      .map((node) => textOf(node).split(/\s+/).map(Number))
      .filter((parts) => parts.length >= 2 && parts.every(Number.isFinite))
      .map((parts) => [parts[0], parts[1]] as [number, number]);
    return coords.length >= 2 ? { type: 'LineString', coordinates: coords } : null;
  }
  return null;
}

function propertiesFrom(placemark: Element): Record<string, unknown> {
  const properties: Record<string, unknown> = {};

  const name = textOf(firstChild(placemark, 'name'));
  if (name) properties.name = name;
  const description = textOf(firstChild(placemark, 'description'));
  if (description) properties.description = description;

  for (const data of descendants(placemark, 'SimpleData')) {
    const key = data.getAttribute('name');
    if (key) properties[key] = textOf(data);
  }
  for (const data of descendants(placemark, 'Data')) {
    const key = data.getAttribute('name');
    if (key) properties[key] = textOf(firstDescendant(data, 'value'));
  }

  return properties;
}

/* ─────────────────────────────── Renderer ───────────────────────────────── */

/** Property carrying each feature's KML style key, for multi-style folders. */
export const KML_STYLE_FIELD = '_kmlStyle';

function normaliseGeometry(type: string | undefined): LayerGeometryType {
  switch ((type ?? '').toLowerCase()) {
    case 'point':
    case 'multipoint':
      return 'point';
    case 'linestring':
    case 'multilinestring':
      return 'line';
    case 'polygon':
    case 'multipolygon':
      return 'polygon';
    default:
      return 'unknown';
  }
}

/** The colour a feature should draw in, given its geometry and KML style. */
function colourFor(style: KmlStyle, geometry: LayerGeometryType): string | undefined {
  if (geometry === 'polygon') return style.fillColor ?? style.lineColor;
  if (geometry === 'point') return style.iconColor;
  return style.lineColor;
}

/**
 * Builds a renderer from the styles seen in one folder.
 *
 * A folder with one style becomes a single-symbol renderer. A folder whose
 * placemarks carry different styles becomes a UNIQUE-VALUE renderer keyed on
 * `_kmlStyle`, which is how per-feature KML colours survive — the app's
 * renderer is per layer, so this is the mechanism that varies colour within one.
 */
function buildRenderer(
  entries: { style: KmlStyle; key: string }[],
  geometry: LayerGeometryType,
): LayerRenderer | undefined {
  const distinct = new Map<string, KmlStyle>();
  for (const entry of entries) if (!distinct.has(entry.key)) distinct.set(entry.key, entry.style);
  if (distinct.size === 0) return undefined;

  const [firstKey] = Array.from(distinct.keys());
  const primary = distinct.get(firstKey) as KmlStyle;

  const base: LayerRenderer = {
    type: 'single',
    color: colourFor(primary, geometry) ?? '#3B82F6',
    fillOpacity:
      geometry === 'polygon'
        ? (primary.filled === false ? 0 : (primary.fillOpacity ?? 0.4))
        : (primary.lineOpacity ?? 1),
    outlineColor: primary.lineColor ?? '#1D4ED8',
    outlineWidth: Math.max(1, Math.round(primary.lineWidth ?? (geometry === 'line' ? 3 : 2))),
    pointSize: Math.round(18 * (primary.iconScale ?? 1)),
    rotation: 0,
    lineStyle: 'solid',
    fillStyle: geometry === 'polygon' && primary.filled === false ? 'transparent' : 'solid',
    outlineStyle: 'solid',
    defaultColor: '#94A3B8',
    // KML icons are external images; the closest gallery equivalent is a pin,
    // which at least keeps the author's colour and scale.
    ...(geometry === 'point' ? { symbolId: primary.iconHref ? 'pin' : 'circle' } : {}),
  };

  if (distinct.size === 1) return base;

  // Keys taken from a styleUrl read as names ("redLine"); keys synthesised from
  // an inline style's colours do not, so those get a plain ordinal instead.
  const categories: RendererCategory[] = Array.from(distinct.entries()).map(
    ([key, style], index) => ({
      value: key,
      label: key.startsWith('#') || key === 'default' ? `Style ${index + 1}` : key,
      color: colourFor(style, geometry) ?? base.color,
    }),
  );

  return { ...base, type: 'unique', field: KML_STYLE_FIELD, categories };
}

/* ─────────────────────────────── Parsing ────────────────────────────────── */

export interface KmlLayer {
  name: string;
  features: GeoJSON.Feature[];
  geometryType: LayerGeometryType;
  renderer?: LayerRenderer;
  /** How many distinct KML styles the folder used. */
  styleCount: number;
  notices: string[];
}

export interface KmlParseResult {
  layers: KmlLayer[];
  notices: string[];
}

/** Turns the placemarks of one folder into a layer. */
function buildLayer(
  name: string,
  placemarks: Element[],
  shared: Map<string, KmlStyle>,
): KmlLayer | null {
  const features: GeoJSON.Feature[] = [];
  const styleEntries: { style: KmlStyle; key: string }[] = [];
  const notices: string[] = [];
  let skipped = 0;

  placemarks.forEach((placemark, index) => {
    const properties = propertiesFrom(placemark);
    const style = styleForPlacemark(placemark, shared);

    // Key features by their styleUrl when present so classes stay readable;
    // otherwise fall back to the resolved colours.
    const ref = textOf(firstChild(placemark, 'styleUrl')).replace(/^#/, '');
    const key =
      ref ||
      [style.lineColor, style.fillColor, style.iconColor].filter(Boolean).join('/') ||
      'default';

    const multi = firstDescendant(placemark, 'MultiGeometry');
    const sources = multi
      ? Array.from(multi.children)
      : (['Point', 'LineString', 'Polygon', 'Track']
          .map((tag) => firstDescendant(placemark, tag))
          .filter(Boolean) as Element[]);

    const geometries = sources
      .map(geometryFrom)
      .filter((geometry): geometry is GeoJSON.Geometry => geometry !== null);

    if (geometries.length === 0) {
      skipped += 1;
      return;
    }

    geometries.forEach((geometry, part) => {
      features.push({
        type: 'Feature',
        id: geometries.length > 1 ? `${index}-${part}` : index,
        geometry,
        properties: { ...properties, [KML_STYLE_FIELD]: key },
      });
      styleEntries.push({ style, key });
    });
  });

  if (features.length === 0) return null;
  if (skipped > 0) {
    notices.push(`${skipped} placemark${skipped === 1 ? '' : 's'} had no geometry.`);
  }

  const kinds = new Set(
    features.map((feature) => normaliseGeometry(feature.geometry?.type)).filter((k) => k !== 'unknown'),
  );
  const geometryType = (kinds.values().next().value as LayerGeometryType) ?? 'unknown';
  if (kinds.size > 1) {
    notices.push(`Mixed geometry (${[...kinds].join(', ')}); styling follows ${geometryType}.`);
  }

  const distinctStyles = new Set(styleEntries.map((entry) => entry.key));

  return {
    name,
    features,
    geometryType,
    renderer: buildRenderer(styleEntries, geometryType),
    styleCount: distinctStyles.size,
    notices,
  };
}

/**
 * Parses KML into one layer per folder.
 *
 * A <Folder> that directly holds placemarks becomes a layer; nested folders
 * recurse and carry a "Parent / Child" name. A KML with no folders yields a
 * single layer named after its <Document>.
 */
export function parseKmlText(text: string, fallbackName = 'KML'): KmlParseResult {
  const doc = new DOMParser().parseFromString(text, 'application/xml');
  if (doc.getElementsByTagName('parsererror').length > 0) {
    throw new Error('This KML is not valid XML.');
  }

  const shared = collectStyles(doc);
  const layers: KmlLayer[] = [];
  const notices: string[] = [];

  const walk = (container: Element, trail: string[]) => {
    const folders = childElements(container, 'Folder');
    const own = childElements(container, 'Placemark');

    if (own.length > 0) {
      const name = trail.length > 0 ? trail.join(' / ') : fallbackName;
      const layer = buildLayer(name, own, shared);
      if (layer) layers.push(layer);
    }

    for (const folder of folders) {
      const label = textOf(firstChild(folder, 'name')) || 'Folder';
      walk(folder, [...trail, label]);
    }
  };

  const roots = descendants(doc, 'Document');
  if (roots.length > 0) {
    for (const root of roots) {
      const label = textOf(firstChild(root, 'name'));
      walk(root, label && roots.length > 1 ? [label] : []);
    }
  } else {
    const kml = doc.documentElement;
    if (kml) walk(kml, []);
  }

  // Fallback: placemarks that live outside any Document/Folder.
  if (layers.length === 0) {
    const all = descendants(doc, 'Placemark');
    if (all.length === 0) throw new Error('No placemarks were found in this KML.');
    const layer = buildLayer(fallbackName, all, shared);
    if (!layer) throw new Error('No placemarks in this KML contained geometry.');
    layers.push(layer);
  }

  if (shared.size === 0) {
    notices.push('This KML defines no styles; default colours are used.');
  }

  return { layers, notices };
}

/* ─────────────────────────────── KMZ (ZIP) ──────────────────────────────── */

export async function extractKmlFromKmz(buffer: ArrayBuffer): Promise<string> {
  const entries = await readZip(buffer);
  const names = Array.from(entries.keys()).filter((name) => /\.kml$/i.test(name));
  if (names.length === 0) throw new Error('This KMZ contains no .kml file.');

  const chosen = names.find((name) => /(^|\/)doc\.kml$/i.test(name)) ?? names[0];
  return new TextDecoder().decode(entries.get(chosen));
}
